import { useCallback, useEffect, useRef, useState } from "react";

interface UseSearchDropdownProps {
  hasResults: boolean;
  isSearching: boolean;
  hasError: boolean;
}

export function useSearchDropdown({
  hasResults,
  isSearching,
  hasError,
}: UseSearchDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Open dropdown when searching, has results, has error, or showing "no results" message
  // Close only when there's nothing to show at all
  useEffect(() => {
    if (isSearching || hasResults || hasError) {
      openDropdown();
    }
    // Don't close automatically - let user read "no results" message
    // Dropdown will close on click outside
  }, [isSearching, hasResults, hasError, openDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) {
        return;
      }

      const target = event.target as Node;

      // Check if click is inside container or dropdown (which might be in portal)
      if (
        containerRef.current.contains(target) ||
        (dropdownRef.current && dropdownRef.current.contains(target))
      ) {
        return;
      }

      closeDropdown();
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, closeDropdown]);

  return {
    isOpen,
    openDropdown,
    closeDropdown,
    containerRef,
    dropdownRef,
  };
}
